import { Component, EventEmitter, Input, Output, OnInit, inject } from '@angular/core'
import { NgClass, NgOptimizedImage, NgStyle, TitleCasePipe } from '@angular/common'
import {
  collapseHorizontallyAnimation,
  fadeInOnEnterAnimation,
  fadeInExpandRightOnEnterAnimation,
  fadeOutCollapseLeftOnLeaveAnimation,
} from 'angular-animations'
import { ResponsiveComponent } from '@nx-superprep/shared'
import { NavigationState, NavigationTabName, NavigationTitleType, NavigationType, UserService } from '@nx-superprep/backend/firebase'
import { isEqual } from 'lodash-es'
import { takeUntil } from 'rxjs'
import { isFirefox } from '@nx-superprep/utils'

export interface NavigationBarState extends NavigationState {
  title: NavigationTitleType,
  collapsed?: boolean,
  segments?: {path:string, title:string}[],
}

export type NavigationTabType = {
  name: NavigationTabName,
  title?: string,
  icon?: {id: string, alt: string, src: string},
}

@Component({
    selector: 'pop-ui-navigationbar',
    standalone: true,
    templateUrl: './navigationbar.component.html',
    styleUrls: ['./navigationbar.component.scss'],
    animations: [
      fadeInOnEnterAnimation({ duration: 300, delay: 50 }),
      collapseHorizontallyAnimation({ duration: 300, delay: 50 }),
      fadeInExpandRightOnEnterAnimation({ duration: 300, delay: 50 }),
      fadeOutCollapseLeftOnLeaveAnimation({ duration: 300, delay: 50 }),
    ],
    imports: [NgClass, NgStyle, NgOptimizedImage, TitleCasePipe]
})
export class NavigationBarComponent extends ResponsiveComponent implements OnInit {

  private _iconchevronleft = { id:'back', alt: 'iconleft', src: '/assets/icons/iconchevronleft.svg' }
  private _iconchevronright = { id:'forward', alt: 'iconright', src: '/assets/icons/iconchevronright.svg' }
  private _iconclose = { id:'close' , alt: 'iconclose', src: '/assets/icons/iconclose.svg' }

  private _tabs: NavigationTabType[] = [
    { name: 'start', icon: this._iconchevronleft },
    { name: 'center' },
    { name: 'end', icon: this._iconchevronright },
  ]

  private _navigationType: NavigationType = 'close'
  private _navigationPosition = 'start'
  private _title: NavigationTitleType

  private _state!: NavigationBarState
  @Input()
  get state() { return this._state; }
  set state(value) {
    this._state = value
    const nt = (this._state.current.type ?? 'root').split('-')
    this._navigationType = nt[0] as NavigationType
    this._navigationPosition = nt[1] ?? 'start' as NavigationTabName
    this._title = this.getTitleForRoute(this._state.title, this._state.current.route, this._navigationType)
    if (this._navigationType === 'root' && this.state.title) {
      this._rootSegment = {path:this.state.current.route.toString(), title:this._title?.toString()??''}
    }
    this.state.action = undefined
    if (this._navigationType === 'root') { this._tabs[0].icon = undefined; this._tabs[2].icon = undefined }
    if (this._navigationType === 'back') { this._tabs[0].icon = this._iconchevronleft; this._tabs[2].icon = undefined }
    if (this._navigationType === 'nav') { this._tabs[0].icon = this._iconchevronleft; this._tabs[2].icon = this._iconchevronright }
    if (this._navigationType === 'close') {
      if (this._navigationPosition !== 'end') {
        this._tabs[0].icon = this._iconclose; this._tabs[2].icon = undefined
      } else {
        this._tabs[0].icon = undefined; this._tabs[2].icon = this._iconclose
      }
    }
    if (!this._title || typeof this._title === 'string') {
      this._tabs[0].title = ['root','back','close'].includes(this._navigationType) ? (this._title as string) : undefined
      this._tabs[1].title = (this._title as string)
      this._tabs[2].title = undefined
    } else {
      this._tabs[0].title = this._title[0]
      this._tabs[1].title = this._title[1]
      this._tabs[2].title = this._title[2]
    }
  }
  @Output() stateChange = new EventEmitter<NavigationBarState>()

  get tabs() { return this._tabs }

  getTitleForRoute(title: NavigationTitleType, route: string | undefined, type: string) {
    if (type === 'root') {
      switch(route) {
        case 'home': return this.segments.length > 1 ? '' : `${this.greeting} ${this.firstName}`
        case 'library': return 'Explore'
        case 'legal': return ''
        default: return route?.toTitleCase()
      }
    } else {
      return title ?? route?.toTitleCase()
    }
  }

  iconStyle(isLast: boolean) {
    return {
      'transform': isLast ? 'translateY(-0.25rem)' : '',
      'padding-right': isFirefox() ? '0.5rem' : ''
    }
  }

  onAction(value: NavigationTabName) {
    if (value === 'start' && ['back','close'].includes(this._navigationType)) {
      this.state.action = 'back'
    } else if (value === 'center' && this._title) {
      this.state.action = 'title'
    } else if (value === 'end') {
      if (this._navigationType === 'nav') {
        this.state.action = 'forward'
      } else if (this._navigationType === 'close') {
        this.state.action = 'close'
      }
    }
    this.stateChange.emit(this.state)
  }

  checkDisabled(i: unknown) {
    return !i
  }

  get isClose() {
    return this._navigationType === 'close'
  }
  get segments() {
    return this.state.segments??[]
  }
  private _rootSegment: { path: string; title: string } | undefined
  get rootSegment() {
    return this.segments.length > 0 ?  this._rootSegment : undefined
  }
  override get isDark() { return !this.isWeb && (this.segments??[]).length === 0 }

  onSelect(segment: {path: string, title: string}, index = -1) {
    //log.debug(segment)
    // global destinations that ignore the title part of the segment
    if (segment.path.toLowerCase() === 'home') { this.router.navigate(['/home'], { replaceUrl:true, skipLocationChange: true }) }
    if (segment.path.toLowerCase() === 'library') { this.router.navigate(['/library'], { replaceUrl:true, skipLocationChange: true }) }
    if (segment.path.toLowerCase() === 'performance') { this.router.navigate(['/performance'], { replaceUrl:true, skipLocationChange: true }) }
    //
    const _found = this.segments.findIndex((s, i) => isEqual(s, segment) && i === index)
    if (1 + _found >= this.segments.length) { return }
    //default  action is to go back
    this.onAction('start')
  }

  greeting = 'Hello,'
  firstName = 'Guest'

  override ngOnInit(): void {
    super.ngOnInit()
    this.userService.getInfo().pipe(takeUntil(this.onDestroy)).subscribe(info => {
      if (!info || this.firstName === info.profileName) { return }
      this.firstName = info.profileName
      this.state = this._state
    })
  }

  readonly userService = inject(UserService)

  constructor() {
    super()
    this.rootClassName = 'navigationbar-root-class-name'
  }
}
